import { useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import {
  useNotificationStore,
  type Notification,
  type NotificationType,
  type NotificationCategory,
} from '@/stores/notification.store';

/** Map API event_type to notification UI type */
function eventToType(eventType: string): NotificationType {
  if (eventType.includes('error') || eventType.includes('critical') || eventType.includes('fail'))
    return 'error';
  if (eventType.includes('warning') || eventType.includes('alert') || eventType.includes('expir'))
    return 'warning';
  if (eventType.includes('success') || eventType.includes('paid') || eventType.includes('resolved'))
    return 'success';
  return 'info';
}

/** Map API event_type to notification category */
function eventToCategory(eventType: string): NotificationCategory {
  if (eventType.startsWith('billing') || eventType.includes('invoice') || eventType.includes('payment'))
    return 'billing';
  if (eventType.startsWith('network') || eventType.includes('alert') || eventType.includes('device'))
    return 'network';
  if (eventType.startsWith('ticket') || eventType.includes('support'))
    return 'ticket';
  if (eventType.startsWith('subscriber') || eventType.includes('subscriber'))
    return 'subscriber';
  return 'system';
}

/** Map API event_type to a href */
function eventToHref(eventType: string): string | undefined {
  if (eventType.includes('billing') || eventType.includes('invoice') || eventType.includes('payment'))
    return '/billing';
  if (eventType.includes('network') || eventType.includes('alert') || eventType.includes('device'))
    return '/noc';
  if (eventType.includes('ticket') || eventType.includes('support'))
    return '/tickets';
  if (eventType.includes('subscriber'))
    return '/subscribers';
  return undefined;
}

interface InboxItem {
  id: string;
  eventType: string;
  title: string;
  body: string | null;
  isRead: boolean;
  createdAt: string;
}

function toNotification(item: InboxItem): Notification {
  return {
    id: item.id,
    type: eventToType(item.eventType),
    category: eventToCategory(item.eventType),
    title: item.title,
    message: item.body ?? '',
    read: item.isRead,
    createdAt: item.createdAt,
    href: eventToHref(item.eventType),
  };
}

const INBOX_POLL_MS = 60_000;

/**
 * Fetches in-app notifications from the inbox API.
 * Polls every 60 seconds while the tab is on screen; a hidden tab catches up when it is shown again.
 */
export function useNotifications() {
  const setNotifications = useNotificationStore((s) => s.setNotifications);
  const lastFetchRef = useRef(0);

  const fetchInbox = useCallback(async () => {
    lastFetchRef.current = Date.now();
    try {
      const res = await api.get('/notifications/inbox?pageSize=30');
      const data = res.data?.data;
      if (!data?.items) return;

      const notifs: Notification[] = data.items.map(toNotification);
      setNotifications(notifs);
    } catch {
      /* non-critical — silently fail */
    }
  }, [setNotifications]);

  useEffect(() => {
    // Every open tab used to poll, hidden ones included: five open tabs cost the inbox five requests a
    // minute to keep one bell current. Only a tab on screen polls now.
    if (!document.hidden) fetchInbox();
    const interval = setInterval(() => {
      if (!document.hidden) fetchInbox();
    }, INBOX_POLL_MS);
    const onVisible = () => {
      if (!document.hidden && Date.now() - lastFetchRef.current >= INBOX_POLL_MS) fetchInbox();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchInbox]);

  return { refetch: fetchInbox };
}
